import { LogLevel } from './LogLevel'
import type { LogRecord } from './types'

/**
 * 性能埋点接口，每个 Logger 通过 `perf` 暴露。
 *
 * 受 logging.perf 独立开关控制：关闭时所有方法为无开销 no-op（直接执行原函数、不计时）。
 */
export interface IProfiler {
  /**
   * 记录调用方已经统计好的耗时，适用于多个交错子任务的聚合结果。
   *
   * @param label - 计时标签
   * @param elapsedMs - 已统计的毫秒数
   */
  record?(label: string, elapsedMs: number): void
  /**
   * 同步计时：返回 end 函数，调用后输出耗时并返回毫秒数。
   *
   * @param label - 计时标签
   * @returns end 函数，调用后返回耗时毫秒数
   */
  time(label: string): () => number
  /**
   * 包裹异步任务计时，返回原 promise 结果；fn reject 时仍记录耗时并 rethrow。
   *
   * @param label - 计时标签
   * @param fn - 被计时的异步任务
   * @returns fn 的结果
   */
  span<T>(label: string, fn: () => Promise<T>): Promise<T>
}

/** perf 关闭时复用的零开销实例，time 返回的 end 函数恒返回 0 */
const NOOP_END = (): number => 0

/**
 * 性能埋点实现。perf 开启时把计时结果以 DEBUG 级别写入 `<ns>:perf` 命名空间，
 * 关闭时退化为无开销直通。
 */
export class Profiler implements IProfiler {
  private readonly _namespace: string
  private readonly _isEnabled: () => boolean
  private readonly _now: () => number
  private readonly _emit: (record: LogRecord) => void

  /**
   * @param namespace - 宿主 Logger 的命名空间，perf 输出落到 `<namespace>:perf`
   * @param isEnabled - 返回 perf 是否启用
   * @param now - 计时时钟，返回毫秒
   * @param emit - 输出 LogRecord 的回调
   */
  constructor(params: {
    namespace: string
    isEnabled: () => boolean
    now: () => number
    emit: (record: LogRecord) => void
  }) {
    this._namespace = params.namespace === '' ? 'perf' : `${params.namespace}:perf`
    this._isEnabled = params.isEnabled
    this._now = params.now
    this._emit = params.emit
  }

  /**
   * 记录调用方已经统计好的聚合耗时，关闭时直接返回。
   *
   * @param label - 计时标签
   * @param elapsedMs - 已统计的毫秒数
   */
  record(label: string, elapsedMs: number): void {
    if (!this._isEnabled()) {
      return
    }
    this._record(label, elapsedMs)
  }

  /**
   * 同步计时，关闭时返回零开销 end。
   *
   * @param label - 计时标签
   * @returns end 函数，调用后输出耗时并返回毫秒数
   */
  time(label: string): () => number {
    if (!this._isEnabled()) {
      return NOOP_END
    }
    const start = this._now()
    return () => {
      const elapsed = this._now() - start
      this._record(label, elapsed)
      return elapsed
    }
  }

  /**
   * 异步计时，异常透传。
   *
   * @param label - 计时标签
   * @param fn - 被计时的异步任务
   * @returns fn 的结果
   */
  async span<T>(label: string, fn: () => Promise<T>): Promise<T> {
    if (!this._isEnabled()) {
      return fn()
    }
    const start = this._now()
    try {
      return await fn()
    } finally {
      this._record(label, this._now() - start)
    }
  }

  /** 输出一条耗时记录 */
  private _record(label: string, elapsed: number): void {
    this._emit({
      level: LogLevel.DEBUG,
      namespace: this._namespace,
      message: `${label} ${elapsed.toFixed(1)}ms`,
      timestamp: this._now(),
    })
  }
}
